import { useEffect, useState } from "react";
import { Button } from "@tremor/react";
import { toast } from "sonner";
import { Copy, Sparkles, Loader2 } from "lucide-react";
import {
  fetchIncidentDiagnosis,
  fetchIncidentSummary,
  fetchIncidentCustomerUpdate,
} from "../api/incidents.js";

const CATEGORY_TONE = {
  region: "bg-amber-50 text-amber-700 ring-amber-200",
  sibling: "bg-red-50 text-red-700 ring-red-200",
  third_party: "bg-purple-50 text-purple-700 ring-purple-200",
  ssl: "bg-blue-50 text-blue-700 ring-blue-200",
  dns: "bg-blue-50 text-blue-700 ring-blue-200",
  domain: "bg-blue-50 text-blue-700 ring-blue-200",
  http: "bg-orange-50 text-orange-700 ring-orange-200",
  browser: "bg-teal-50 text-teal-700 ring-teal-200",
  timeout: "bg-gray-100 text-gray-700 ring-gray-200",
  recent_change: "bg-purple-50 text-purple-700 ring-purple-200",
};

const CONFIDENCE_TONE = {
  high: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  medium: "bg-amber-100 text-amber-700 ring-amber-200",
  low: "bg-gray-100 text-gray-600 ring-gray-200",
};

function copyToClipboard(text, label = "Copied") {
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success(label))
    .catch(() => toast.error("Copy failed"));
}

function CustomerUpdateModal({ incidentId, onClose }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIncidentCustomerUpdate(incidentId)
      .then((d) => setText(d.text))
      .catch(() => toast.error("Failed to load update"))
      .finally(() => setLoading(false));
  }, [incidentId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Customer update draft
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Edit before publishing — this is a starting point, not a finished post.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            <textarea
              rows={10}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono outline-none focus:border-teal-600"
            />
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
              <Button
                icon={Copy}
                onClick={() => copyToClipboard(text, "Customer update copied")}
              >
                Copy
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function IncidentDiagnosis({ incident }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [showCustomerUpdate, setShowCustomerUpdate] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchIncidentDiagnosis(incident.id)
      .then(setReport)
      .catch(() => toast.error("Failed to load diagnosis"))
      .finally(() => setLoading(false));
  }, [incident.id]);

  async function copySummary() {
    setCopying(true);
    try {
      const d = await fetchIncidentSummary(incident.id);
      copyToClipboard(d.markdown, "Incident summary copied");
    } catch {
      toast.error("Failed to load summary");
    } finally {
      setCopying(false);
    }
  }

  if (loading) {
    return (
      <div className="border-b border-gray-100 px-6 py-4">
        <p className="text-xs text-gray-400">Diagnosing…</p>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <div className="border-b border-gray-100 px-6 py-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <Sparkles className="h-3.5 w-3.5 text-teal-600" />
          Diagnosis
        </h3>
        <div className="flex items-center gap-1.5">
          <Button
            size="xs"
            variant="secondary"
            icon={Copy}
            loading={copying}
            onClick={copySummary}
          >
            Copy summary
          </Button>
          <Button
            size="xs"
            variant="primary"
            onClick={() => setShowCustomerUpdate(true)}
          >
            Customer update
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
        <div className="flex items-start gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ${
              CONFIDENCE_TONE[report.confidence] ?? CONFIDENCE_TONE.low
            }`}
          >
            {report.confidence} confidence
          </span>
          <p className="flex-1 text-sm font-semibold text-gray-900">
            {report.likely_cause}
          </p>
        </div>
        {report.first_failing_location && (
          <p className="mt-1 text-xs text-gray-500">
            First failing location: {report.first_failing_location}
          </p>
        )}

        {report.evidence?.length > 0 && (
          <div className="mt-3">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
              Evidence
            </p>
            <ul className="space-y-1">
              {report.evidence.map((e, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ${
                      CATEGORY_TONE[e.category] ?? CATEGORY_TONE.timeout
                    }`}
                  >
                    {e.category}
                  </span>
                  <span className="text-gray-700">{e.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {report.suggested_actions?.length > 0 && (
          <div className="mt-3">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
              Suggested next actions
            </p>
            <ul className="space-y-0.5 text-xs text-gray-700">
              {report.suggested_actions.map((a, i) => (
                <li key={i}>
                  {a.href ? (
                    <a
                      href={a.href}
                      className="text-teal-700 hover:underline"
                    >
                      {a.label}
                    </a>
                  ) : (
                    a.label
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {showCustomerUpdate && (
        <CustomerUpdateModal
          incidentId={incident.id}
          onClose={() => setShowCustomerUpdate(false)}
        />
      )}
    </div>
  );
}
